import { useState } from "react";
import { motion } from "framer-motion";
import { Sparkles, Check, ChevronDown, Calendar, Star, Crown } from "lucide-react";

const SUBSCRIPTION_PLANS = [
  {
    id: "sub-monthly",
    name: "Monthly Care Plan",
    tag: "Regular Monthly Pampering 🗓️",
    summary: "2 Premium Grooming sessions per month, nail trimming & ear cleaning.",
    badge: "Monthly Pass",
    bannerBg: "bg-[#00a99d]", // SniffyNose Teal
    price: "₹2,999",
    period: "/month",
    image: "/Woofsy - Monthly Care Plan.png",
    inclusions: [
      "2 Premium Grooming Sessions",
      "Nail Trimming & Smooth Filing",
      "Ear Cleaning & Sanitization",
      "Coat Perfume Finish",
      "Priority Slot Booking",
    ],
  },
  {
    id: "sub-quarterly",
    name: "Quarterly Care Plan",
    tag: "3 Months Complete Grooming 🌟",
    summary: "6 Premium sessions over 3 months + 1 free spa upgrade & 10% OFF.",
    badge: "Best Value",
    bannerBg: "bg-[#f5a623]", // SniffyNose Gold
    price: "₹7,999",
    period: "/quarter",
    image: "/Woofsy - Quarterly Care plan.png",
    inclusions: [
      "6 Premium Grooming Sessions",
      "One Complimentary Spa Upgrade",
      "10% OFF on all extra add-ons",
      "Free Nail Trimming with every visit",
      "Ear Cleaning & Sanitization",
      "Priority Slot Booking",
    ],
  },
  {
    id: "sub-annual",
    name: "Annual VIP Care Plan",
    tag: "Year-Round VIP Royal Treatment 👑",
    summary: "12 Premium sessions per year + 2 free spa upgrades & 15% OFF.",
    badge: "VIP Pass",
    bannerBg: "bg-[#7c3aed]", // Royal Purple
    price: "₹14,999",
    period: "/year",
    image: "/Woofsy - Annual VIP Care plan.png",
    inclusions: [
      "12 Premium Grooming Sessions",
      "2 Complimentary Spa Upgrades",
      "15% OFF on all services & add-ons",
      "Unlimited Priority Booking & Same-Day Slot",
      "Deluxe Birthday Spa & Gift Goodies",
      "VIP Dedicated Support Specialist",
    ],
  },
];

export default function GroomingSubscriptions({ onBook }) {
  const [expandedCardId, setExpandedCardId] = useState(null);

  const toggleCard = (id) => {
    setExpandedCardId((prev) => (prev === id ? null : id));
  };

  return (
    <section id="subscriptions" data-testid="subscriptions-section" className="relative py-16 sm:py-24 px-5 sm:px-8 bg-gradient-to-b from-orange-50/40 via-amber-50/20 to-white">
      <div className="max-w-7xl mx-auto">
        
        {/* Section Header */}
        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          className="text-center max-w-3xl mx-auto mb-12">
          <span className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-woofsy-primary/10 text-woofsy-primary text-xs font-bold uppercase tracking-wider mb-3 border border-woofsy-primary/20">
            <Calendar className="w-3.5 h-3.5" />
            <span>Regular Care & Max Savings</span>
          </span>
          <h2 className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl text-slate-900 mb-3">
            Grooming Subscriptions & Care Plans
          </h2>
          <p className="text-slate-600 text-base sm:text-lg font-medium">
            Subscribe to regular grooming care for your dogs in Pune with free spa upgrades, priority slots & max savings.
          </p>
        </motion.div>

        {/* Subscription Cards Grid (items-start for single-card expansion) */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8 items-start">
          {SUBSCRIPTION_PLANS.map((p, i) => {
            const isExpanded = expandedCardId === p.id;
            return (
              <motion.div key={p.id} data-testid={`subscription-card-${p.id}`}
                initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
                whileHover={{ y: -6 }}
                className="bg-white rounded-3xl overflow-hidden shadow-md hover:shadow-2xl border border-slate-200/80 flex flex-col justify-between transition-all duration-300 group">
                
                {/* 1. Top Image Banner */}
                <div className="relative aspect-square w-full overflow-hidden bg-slate-100">
                  <img src={p.image} alt={p.name} className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500" />
                  
                  {/* Top Right Pill Badge */}
                  <span className="absolute top-3 right-3 text-xs font-bold px-3 py-1 rounded-full bg-white/95 text-slate-800 shadow-md backdrop-blur-md flex items-center gap-1">
                    <span className="text-amber-500 font-extrabold">📅</span>
                    <span>{p.badge}</span>
                  </span>
                </div>

                {/* 2. Middle Colored Summary Strip */}
                <div className={`px-4 py-2.5 text-xs font-semibold text-white flex items-center gap-2 ${p.bannerBg}`}>
                  <Sparkles className="w-3.5 h-3.5 shrink-0" />
                  <span className="leading-tight line-clamp-1">{p.summary}</span>
                </div>

                {/* 3. Bottom White Card Content */}
                <div className="p-5 flex flex-col justify-between flex-1">
                  <div>
                    {/* Title & What Includes Link */}
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <h3 className="font-heading font-extrabold text-lg text-slate-900 leading-snug">{p.name}</h3>
                      <button
                        onClick={() => toggleCard(p.id)}
                        data-testid={`sub-what-includes-${p.id}`}
                        className="text-xs font-bold text-sky-600 hover:text-sky-700 underline shrink-0 whitespace-nowrap flex items-center gap-0.5 transition-colors"
                      >
                        <span>What Includes?</span>
                        <motion.div animate={{ rotate: isExpanded ? 180 : 0 }} transition={{ duration: 0.2 }}>
                          <ChevronDown className="w-3.5 h-3.5" />
                        </motion.div>
                      </button>
                    </div>

                    {/* Price */}
                    <div className="text-xs text-slate-600 font-semibold mb-4">
                      <span>From <strong className="text-base text-slate-900 font-extrabold">{p.price}</strong></span>
                      <span className="text-slate-500 font-medium"> {p.period}</span>
                    </div>

                    {/* Expanded Inclusions Dropdown */}
                    {isExpanded && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        className="mb-4 p-3.5 rounded-2xl bg-slate-50 border border-slate-200/80 space-y-2 text-xs"
                      >
                        <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">Subscription Perks:</p>
                        {p.inclusions.map((inc, idx) => (
                          <div key={idx} className="flex items-start gap-2 text-slate-700 font-medium">
                            <Check className="w-3.5 h-3.5 text-emerald-600 shrink-0 mt-0.5 font-bold" />
                            <span className="leading-snug">{inc}</span>
                          </div>
                        ))}
                      </motion.div>
                    )}
                  </div>

                  {/* Subscribe Now Pill Button */}
                  <button
                    data-testid={`btn-subscribe-${p.id}`}
                    onClick={() => onBook(p.title)}
                    className="w-full py-3.5 rounded-2xl bg-woofsy-primary hover:bg-[#e8792c] text-white font-heading font-extrabold text-sm shadow-md shadow-orange-500/20 hover:shadow-lg hover:shadow-orange-500/30 transition-all text-center"
                  >
                    Subscribe Now
                  </button>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
